import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import ReactMarkdown from "react-markdown";
import {
  Loader2,
  MessageSquare,
  Send,
  Sparkles,
  Clock,
  CheckCircle2,
  Anchor,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  forumList,
  forumPost,
  forumAnswer,
  forumDismiss,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { TranslateButton } from "../components/TranslateButton";

const MAX_POST = 1000;
const MAX_ANSWER = 4000;

function relativeTime(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function QuestionCard({ item, isArchitect, onAnswered, onDismissed }) {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const submit = async () => {
    const trimmed = reply.trim();
    if (trimmed.length < 2 || sending) return;
    setSending(true);
    try {
      const updated = await forumAnswer(item.id, trimmed);
      toast.success("Reply posted.");
      setReply("");
      setOpen(false);
      onAnswered?.(updated);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't post the reply.");
    } finally {
      setSending(false);
    }
  };

  const dismiss = async () => {
    if (dismissing) return;
    const ok = window.confirm(
      "Dismiss this question? It will be hidden from the public thread and your inbox. The record is preserved in the audit log."
    );
    if (!ok) return;
    setDismissing(true);
    try {
      await forumDismiss(item.id);
      toast.success("Question dismissed.");
      onDismissed?.(item.id);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't dismiss the question.");
    } finally {
      setDismissing(false);
    }
  };

  return (
    <article
      className="border border-zinc-800 bg-[#0b0b0d] p-5"
      data-testid={`forum-question-${item.id}`}
    >
      <header className="flex items-center justify-between gap-3 mb-3 text-[10px] uppercase tracking-[0.3em]">
        <div className="flex items-center gap-2 text-zinc-400">
          <MessageSquare className="h-3 w-3 text-emerald-400" />
          <span className="text-zinc-200">{item.operator_handle}</span>
          <span className="text-zinc-600">·</span>
          <Clock className="h-3 w-3 text-zinc-600" />
          <span className="text-zinc-500 normal-case tracking-normal text-[11px]">
            {relativeTime(item.created_at)}
          </span>
        </div>
        {item.answered ? (
          <span
            className="inline-flex items-center gap-1.5 text-emerald-400"
            data-testid={`forum-answered-${item.id}`}
            title="Answered — green light"
          >
            <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
            <CheckCircle2 className="h-3 w-3" /> Answered
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 text-rose-400"
            data-testid={`forum-awaiting-${item.id}`}
            title="Awaiting reply — red light"
          >
            <span className="h-2 w-2 rounded-full bg-rose-400 shadow-[0_0_8px_rgba(251,113,133,0.8)] animate-sentinel-pulse" />
            Awaiting
          </span>
        )}
      </header>

      <div className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed">
        {item.body}
      </div>
      <TranslateButton
        text={item.body}
        inline
        testIdPrefix={`translate-forum-${item.id}`}
        className="mt-2"
      />

      {item.answered && item.answer && (
        <div
          className="mt-5 border-l-2 border-emerald-500/50 pl-4 py-2"
          data-testid={`forum-answer-${item.id}`}
        >
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.3em] text-emerald-400">
            <Anchor className="h-3 w-3" /> The Architect ·{" "}
            <span className="text-zinc-500 normal-case tracking-normal text-[11px]">
              {relativeTime(item.answered_at)}
            </span>
          </div>
          <div className="prose prose-invert prose-sm max-w-none text-zinc-200 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <ReactMarkdown>{item.answer}</ReactMarkdown>
          </div>
        </div>
      )}

      {isArchitect && !item.answered && (
        <div className="mt-4">
          {!open ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid={`forum-reply-btn-${item.id}`}
                className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.3em] border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 transition-colors"
              >
                <Send className="h-3 w-3" /> Reply as Architect
              </button>
              <button
                type="button"
                onClick={dismiss}
                disabled={dismissing}
                data-testid={`forum-dismiss-btn-${item.id}`}
                className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.3em] border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40 transition-colors"
              >
                {dismissing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
                Dismiss
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value.slice(0, MAX_ANSWER))}
                rows={5}
                placeholder="Write your reply — markdown supported (**bold**, *italic*, lists, links)…"
                data-testid={`forum-reply-input-${item.id}`}
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body resize-y text-zinc-100 placeholder:text-zinc-600"
                disabled={sending}
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-600">
                  {reply.length}/{MAX_ANSWER}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setOpen(false); setReply(""); }}
                    disabled={sending}
                    className="px-3 py-2 text-[10px] uppercase tracking-[0.3em] border border-zinc-800 text-zinc-500 hover:text-zinc-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={sending || reply.trim().length < 2}
                    data-testid={`forum-reply-send-${item.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.3em] border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:border-zinc-800 disabled:text-zinc-700 disabled:hover:bg-transparent transition-colors"
                  >
                    {sending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Send className="h-3 w-3" />
                    )}
                    Post reply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {isArchitect && item.answered && (
        <div className="mt-4 pt-3 border-t border-zinc-900 flex justify-end">
          <button
            type="button"
            onClick={dismiss}
            disabled={dismissing}
            data-testid={`forum-dismiss-btn-${item.id}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-rose-300 disabled:opacity-40 transition-colors"
          >
            {dismissing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
            Dismiss from thread
          </button>
        </div>
      )}
    </article>
  );
}

export default function Forum() {
  const { user } = useAuth();
  const isArchitect = !!user?.is_architect;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [body, setBody] = useState("");

  const refresh = async () => {
    try {
      const data = await forumList(50, 0);
      setItems(data.items || []);
    } catch (err) {
      console.warn("[Forum] list failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = body.trim();
    if (trimmed.length < 8 || posting) return;
    setPosting(true);
    try {
      const doc = await forumPost(trimmed);
      toast.success("Question posted — the Architect will be back.");
      setBody("");
      setItems((prev) => [doc, ...prev]);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg =
        typeof d === "object" && d?.message
          ? d.message
          : typeof d === "string"
          ? d
          : "Couldn't post the question.";
      toast.error(msg);
    } finally {
      setPosting(false);
    }
  };

  const onAnswered = (updated) => {
    setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
  };

  const onDismissed = (qid) => {
    setItems((prev) => prev.filter((it) => it.id !== qid));
  };

  return (
    <AppShell>
      <section
        className="min-h-[calc(100vh-120px)] bg-[#09090b] text-zinc-100 font-body"
        data-testid="forum-page"
      >
        <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
          <header className="flex items-start gap-4">
            <div className="h-12 w-12 shrink-0 border border-emerald-500/40 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-display tracking-tight">
                Ask the Architect
              </h1>
              <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
                {isArchitect ? (
                  <>
                    Every new question lands here. Replies are markdown and
                    appear publicly under the original post — pin, teach,
                    clarify, or just say hi.
                  </>
                ) : (
                  <>
                    Leave a question for the Architect — about Projectivy
                    Sentinel, the 8-layer protocol, the Orion Engine, or
                    anything else on your mind. Every question passes
                    quietly through the same non-harm pipeline as chat, so
                    the Sentinel can hold space for the heavy stuff too.
                    Replies land publicly below your post when the
                    Architect answers. 3 questions a day per browser.
                  </>
                )}
              </p>
            </div>
          </header>

          {!isArchitect && (
            <form
              onSubmit={submit}
              className="border border-zinc-800 bg-[#0b0b0d] p-5 space-y-3"
              data-testid="forum-composer"
            >
              <label
                htmlFor="forum-body"
                className="text-[10px] uppercase tracking-[0.3em] text-zinc-500"
              >
                Your question
              </label>
              <textarea
                id="forum-body"
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, MAX_POST))}
                placeholder="What would you like to ask the Architect?"
                rows={4}
                disabled={posting}
                data-testid="forum-body-input"
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 outline-none p-3 text-sm resize-y font-body placeholder:text-zinc-600 disabled:opacity-40"
              />
              <div className="flex items-center justify-between flex-wrap gap-3">
                <span className="text-[10px] text-zinc-600">
                  {body.length}/{MAX_POST} · at least 8 characters
                </span>
                <button
                  type="submit"
                  disabled={posting || body.trim().length < 8}
                  data-testid="forum-submit-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.25em] border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:border-zinc-800 disabled:text-zinc-700 disabled:hover:bg-transparent transition-colors"
                >
                  {posting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Posting
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" /> Post question
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          <div
            className="space-y-4"
            data-testid="forum-thread"
          >
            {loading ? (
              <div className="text-center py-10 text-zinc-600 text-xs uppercase tracking-[0.3em]">
                Loading thread…
              </div>
            ) : items.length === 0 ? (
              <div className="border border-dashed border-zinc-800 bg-[#0b0b0d] p-10 text-center text-xs text-zinc-500">
                {isArchitect
                  ? "No questions yet. When visitors post, they'll appear here."
                  : "No questions yet — yours could be the first."}
              </div>
            ) : (
              items.map((it) => (
                <QuestionCard
                  key={it.id}
                  item={it}
                  isArchitect={isArchitect}
                  onAnswered={onAnswered}
                  onDismissed={onDismissed}
                />
              ))
            )}
          </div>
        </div>
      </section>
    </AppShell>
  );
}
